import React, { useState, useEffect } from 'react';
import { listingAPI, productAPI, activityAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { ProfilePhoto } from '../ProfilePhoto';
import { Coins, Check, X, Clock, User, MessageCircle, ExternalLink, ShoppingBag, Store, Target, Zap, UsersRound, Wallet, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { formatCoins, formatDateTime } from '../../lib/utils';

export default function ApprovalsTab({ classId, onRefresh }) {
  const [pendingListings, setPendingListings] = useState([]);
  const [pendingPurchases, setPendingPurchases] = useState([]);
  const [pendingActivities, setPendingActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState('activities');
  const [synced, setSynced] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, activityId: null, studentId: null, studentName: '', activityTitle: '' });
  const [rejectionNote, setRejectionNote] = useState('');

  useEffect(() => {
    syncAndLoad();
  }, [classId]);

  const syncAndLoad = async () => {
    try {
      // Auto-sync stale statuses before loading
      if (!synced) {
        await activityAPI.syncActivityStatuses(classId);
        setSynced(true);
      }
    } catch { /* sync is best-effort */ }
    await loadPendingItems();
  };

  const loadPendingItems = async () => {
    try {
      const [listingsRes, purchasesRes, activitiesRes] = await Promise.all([
        listingAPI.list(classId, 'PENDING'),
        productAPI.getPendingPurchases(classId),
        activityAPI.getPendingActivityApprovals(classId),
      ]);
      setPendingListings(listingsRes.data);
      setPendingPurchases(purchasesRes.data);
      setPendingActivities(activitiesRes.data);
    } catch {
      toast.error('Error al cargar aprobaciones pendientes');
    } finally {
      setLoading(false);
    }
  };

  // Listing handlers
  const handleApproveListing = async (listingId) => {
    setProcessingId(listingId);
    try {
      await listingAPI.approve(classId, listingId);
      toast.success('Anuncio aprobado');
      loadPendingItems(); onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    } finally { setProcessingId(null); }
  };

  const handleRejectListing = async (listingId) => {
    if (!window.confirm('¿Rechazar este anuncio?')) return;
    setProcessingId(listingId);
    try {
      await listingAPI.reject(classId, listingId);
      toast.success('Anuncio rechazado');
      loadPendingItems(); onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    } finally { setProcessingId(null); }
  };

  // Purchase handlers
  const handleApprovePurchase = async (purchaseId) => {
    setProcessingId(purchaseId);
    try {
      await productAPI.approvePurchase(classId, purchaseId);
      toast.success('Compra aprobada');
      loadPendingItems(); onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    } finally { setProcessingId(null); }
  };

  const handleRejectPurchase = async (purchaseId) => {
    if (!window.confirm('¿Rechazar esta compra? El estudiante no perderá sus coins.')) return;
    setProcessingId(purchaseId);
    try {
      await productAPI.rejectPurchase(classId, purchaseId);
      toast.success('Compra rechazada');
      loadPendingItems(); onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    } finally { setProcessingId(null); }
  };

  // Activity approval handlers
  const handleApproveActivity = async (activityId, studentId) => {
    const key = `${activityId}-${studentId}`;
    setProcessingId(key);
    try {
      await activityAPI.approveCompletion(classId, activityId, studentId, 'approve');
      toast.success('Actividad aprobada - coins y XP otorgados');
      loadPendingItems(); onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    } finally { setProcessingId(null); }
  };

  const openRejectDialog = (activityId, studentId, studentName, activityTitle) => {
    setRejectDialog({ open: true, activityId, studentId, studentName, activityTitle });
    setRejectionNote('');
  };

  const confirmRejectActivity = async () => {
    const { activityId, studentId } = rejectDialog;
    const key = `${activityId}-${studentId}`;
    setProcessingId(key);
    try {
      await activityAPI.approveCompletion(classId, activityId, studentId, 'reject', rejectionNote);
      toast.success('Entrega rechazada con nota enviada al estudiante');
      setRejectDialog({ open: false, activityId: null, studentId: null, studentName: '', activityTitle: '' });
      loadPendingItems(); onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    } finally { setProcessingId(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalPending = pendingListings.length + pendingPurchases.length + pendingActivities.length;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-heading font-bold text-slate-900">Aprobaciones Pendientes</h3>
        <p className="text-slate-500">Revisa y aprueba actividades, anuncios y compras de estudiantes</p>
      </div>

      {totalPending === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Check className="w-12 h-12 text-emerald-300 mb-4" />
            <p className="text-slate-500">No hay aprobaciones pendientes</p>
            <p className="text-sm text-slate-400">Todo al día!</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="activities" className="relative" data-testid="approvals-tab-activities">
              <Target className="w-4 h-4 mr-2" />
              Actividades
              {pendingActivities.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-emerald-500 text-white rounded-full">
                  {pendingActivities.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="listings" className="relative" data-testid="approvals-tab-listings">
              <Store className="w-4 h-4 mr-2" />
              Anuncios
              {pendingListings.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                  {pendingListings.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="purchases" className="relative" data-testid="approvals-tab-purchases">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Compras
              {pendingPurchases.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                  {pendingPurchases.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Activities Tab */}
          <TabsContent value="activities" className="mt-4">
            {pendingActivities.length === 0 ? (
              <Card className="border-dashed border-2 border-slate-200">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Target className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay actividades pendientes de aprobación</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {pendingActivities.map((item) => {
                  const key = `${item.activity_id}-${item.student_user_id}`;
                  return (
                    <Card key={key} className="border-slate-200" data-testid={`pending-activity-${key}`}>
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <ProfilePhoto
                              photoUrl={item.student_photo_url}
                              name={item.student_name}
                              size={40}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900 truncate">{item.student_name}</p>
                              <p className="text-sm text-slate-500 truncate">completó: <strong>{item.activity_title}</strong></p>
                              <div className="flex items-center gap-3 mt-1 flex-wrap">
                                <div className="flex items-center gap-1 text-amber-600 text-xs font-medium">
                                  <Coins className="w-3 h-3" />{item.activity_coin_value} coins
                                  {item.is_group_activity && (
                                    <span className="text-purple-600 ml-1 flex items-center gap-0.5">
                                      <Wallet className="w-3 h-3" /> wallet grupal
                                    </span>
                                  )}
                                </div>
                                {item.activity_xp_value > 0 && (
                                  <div className="flex items-center gap-1 text-purple-600 text-xs font-medium">
                                    <Zap className="w-3 h-3" />{item.activity_xp_value} XP
                                  </div>
                                )}
                                {item.is_group_activity && (
                                  <Badge variant="outline" className="text-xs border-purple-300 text-purple-600">
                                    <UsersRound className="w-3 h-3 mr-1" />{item.group_name || 'Grupal'}
                                  </Badge>
                                )}
                                <span className="text-xs text-slate-400">
                                  <Clock className="w-3 h-3 inline mr-1" />{formatDateTime(item.submitted_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              className="bg-emerald-600 hover:bg-emerald-700"
                              size="sm"
                              disabled={processingId === key}
                              onClick={() => handleApproveActivity(item.activity_id, item.student_user_id)}
                              data-testid={`approve-activity-${key}`}
                            >
                              <Check className="w-4 h-4 mr-1" />Aprobar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50 border-red-200"
                              disabled={processingId === key}
                              onClick={() => openRejectDialog(item.activity_id, item.student_user_id, item.student_name, item.activity_title)}
                              data-testid={`reject-activity-${key}`}
                            >
                              <X className="w-4 h-4 mr-1" />Rechazar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings" className="mt-4">
            {pendingListings.length === 0 ? (
              <Card className="border-dashed border-2 border-slate-200">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Store className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay anuncios pendientes</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingListings.map((listing) => (
                  <Card key={listing.id} className="border-slate-200" data-testid={`pending-listing-${listing.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row gap-6">
                        {listing.image_url && (
                          <div className="w-full lg:w-48 h-32 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0">
                            <img src={listing.image_url} alt={listing.title} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div>
                              <h4 className="font-heading font-bold text-lg text-slate-900">{listing.title}</h4>
                              <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                                <User className="w-4 h-4" /><span>{listing.seller_name}</span>
                                <span className="text-slate-300">&#183;</span>
                                <Clock className="w-4 h-4" /><span>{formatDateTime(listing.created_at)}</span>
                              </div>
                            </div>
                            <Badge className="bg-amber-50 text-amber-700 border-amber-100">Pendiente</Badge>
                          </div>
                          <p className="text-slate-600 mb-4">{listing.description}</p>
                          <div className="flex flex-wrap items-center gap-4 mb-4">
                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full font-bold">
                              <Coins className="w-4 h-4" />{formatCoins(listing.price_coins)}
                            </div>
                            {listing.category && <Badge variant="secondary">{listing.category}</Badge>}
                            {listing.contact_method && (
                              <span className="text-sm text-slate-500 flex items-center gap-1">
                                <MessageCircle className="w-4 h-4" />{listing.contact_method}
                              </span>
                            )}
                            {listing.image_url && (
                              <a href={listing.image_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                <ExternalLink className="w-4 h-4" />Ver imagen
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={processingId === listing.id} onClick={() => handleApproveListing(listing.id)} data-testid={`approve-listing-${listing.id}`}>
                              <Check className="w-4 h-4 mr-2" />Aprobar
                            </Button>
                            <Button variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" disabled={processingId === listing.id} onClick={() => handleRejectListing(listing.id)} data-testid={`reject-listing-${listing.id}`}>
                              <X className="w-4 h-4 mr-2" />Rechazar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Purchases Tab */}
          <TabsContent value="purchases" className="mt-4">
            {pendingPurchases.length === 0 ? (
              <Card className="border-dashed border-2 border-slate-200">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <ShoppingBag className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay compras pendientes de aprobación</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingPurchases.map((purchase) => (
                  <Card key={purchase.id} className="border-slate-200" data-testid={`pending-purchase-${purchase.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-6">
                        {purchase.product_image_url && (
                          <div className="w-20 h-20 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0">
                            <img src={purchase.product_image_url} alt={purchase.product_name} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <ProfilePhoto photoUrl={purchase.buyer_photo_url} name={purchase.buyer_name} size={32} />
                            <div>
                              <p className="font-medium text-slate-900">{purchase.buyer_name}</p>
                              <p className="text-sm text-slate-500">quiere comprar</p>
                            </div>
                          </div>
                          <h4 className="font-heading font-bold text-lg text-slate-900">{purchase.product_name}</h4>
                          <div className="flex items-center gap-4 mt-2">
                            <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-full font-bold text-sm">
                              <Coins className="w-4 h-4" />{formatCoins(purchase.price_paid)}
                            </div>
                            <span className="text-sm text-slate-500">{formatDateTime(purchase.created_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={processingId === purchase.id} onClick={() => handleApprovePurchase(purchase.id)} data-testid={`approve-purchase-${purchase.id}`}>
                            <Check className="w-4 h-4 mr-2" />Aprobar
                          </Button>
                          <Button variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" disabled={processingId === purchase.id} onClick={() => handleRejectPurchase(purchase.id)} data-testid={`reject-purchase-${purchase.id}`}>
                            <X className="w-4 h-4 mr-2" />Rechazar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Rejection Note Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(v) => { if (!v) setRejectDialog({ ...rejectDialog, open: false }); }}>
        <DialogContent className="max-w-md" data-testid="rejection-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Rechazar Entrega
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-500">Estudiante</p>
              <p className="font-medium text-slate-900">{rejectDialog.studentName}</p>
              <p className="text-sm text-slate-500 mt-1">Actividad</p>
              <p className="font-medium text-slate-900">{rejectDialog.activityTitle}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Nota para el estudiante <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <Textarea
                value={rejectionNote}
                onChange={(e) => setRejectionNote(e.target.value)}
                placeholder="Explica qué debe corregir el estudiante para que su entrega sea aprobada..."
                rows={3}
                className="resize-none"
                data-testid="rejection-note-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRejectDialog({ ...rejectDialog, open: false })} data-testid="cancel-reject-btn">
              Cancelar
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmRejectActivity} data-testid="confirm-reject-btn">
              <X className="w-4 h-4 mr-1" />Rechazar Entrega
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
